import React, { useCallback, useEffect, useState } from "react";
import { Table, Spinner, Form, Button, Modal, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import API from "../services/api";
import "./SupplierDispatchHistory.css";

const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

const statusPill = (status) => {
  const s = String(status || "draft").toLowerCase();
  return `sdh-pill ${s}`;
};

const money = (value) => `INR ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const workflowStatus = (row = {}) => row.plantWorkflow?.status || row.approvalStatus || "draft";
const gateQrUrl = (code) => code ? `https://api.qrserver.com/v1/create-qr-code/?size=90x90&format=png&margin=1&data=${encodeURIComponent(code)}` : "";
const canRespondQuality = (row = {}) =>
  ["plant-rejected", "grn-ready"].includes(row.plantWorkflow?.status) && row.plantWorkflow?.supplierResponse !== "agreed";

const downloadQualityReport = (row = {}) => {
  const q = row.qualityInspection || {};
  const doc = new jsPDF("p", "mm", "a4");
  const left = 14;
  let y = 16;
  doc.setFontSize(15);
  doc.text("Lotus Rice Mill Pvt. Ltd.", left, y);
  y += 8;
  doc.setFontSize(12);
  doc.text("Supplier Material Quality Report", left, y);
  y += 8;
  doc.setFontSize(9);
  [
    `Report No: ${q.reportNo || "-"}`,
    `Supplier: ${row.companyName || row.supplierName || "-"}`,
    `Vehicle: ${row.vehicle?.number || "-"}`,
    `Material: ${row.materialName || row.productName || "-"}`,
    `PO: ${row.purchaseOrderNo || "-"}`,
    `Dispatch Qty: ${row.quantity || "-"} ${row.unit || ""}`,
    `Quality Result: ${q.status || "pending"}`,
    `Accepted Qty: ${q.acceptedQty ?? "-"}`,
    `Rejected Qty: ${q.rejectedQty ?? "-"}`,
    `Final Payable Amount: ${money(q.proposedAmount)}`,
    `Plant Remarks: ${row.plantWorkflow?.plantRemarks || "-"}`,
  ].forEach((line) => {
    doc.text(line, left, y);
    y += 6;
  });
  y += 4;
  doc.setFontSize(10);
  doc.text("Quality Parameters", left, y);
  y += 6;
  doc.setFontSize(8);
  (q.qualityChecks || []).forEach((item, index) => {
    const line = `${index + 1}. ${item.label || "-"} | Expected: ${item.expectedPercent ?? "-"}% | Actual: ${item.actualValue || "-"} | Result: ${item.result || "-"}`;
    const split = doc.splitTextToSize(line, 180);
    doc.text(split, left, y);
    y += split.length * 4 + 2;
    if (y > 275) {
      doc.addPage();
      y = 16;
    }
  });
  doc.save(`Quality_Report_${q.reportNo || row.vehicle?.number || row._id}.pdf`);
};

const SupplierDispatchHistory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [responseRow, setResponseRow] = useState(null);
  const [responseForm, setResponseForm] = useState({ response: "agreed", remarks: "" });
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/supplier/dispatches", {
        params: status === "all" ? {} : { status },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load dispatches", err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="sdh-page">
      <div className="sdh-container">
        {message ? <Alert variant="info">{message}</Alert> : null}
        <div className="sdh-topbar">
          <div>
            <div className="sdh-title">Dispatch History</div>
            <div className="sdh-subtitle">Track all your dispatch requests</div>
          </div>
          <Form.Select
            size="sm"
            className="sdh-filter-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </Form.Select>
        </div>

        {loading ? (
          <div className="text-center mt-4"><Spinner animation="border" /></div>
        ) : (
          <>
            <div className="sdh-card d-none d-md-block">
              <div className="table-responsive">
                <Table hover responsive className="sdh-table mb-0">
                  <thead>
                    <tr>
                      <th>Dispatch Date</th>
                      <th>Material</th>
                      <th>Qty</th>
                      <th>Vehicle</th>
                      <th>Status</th>
                      <th>Quality</th>
                      <th>Amount</th>
                      <th>Gate Pass / GRN</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={10} className="text-center text-muted">No dispatches found.</td></tr>
                    ) : (
                      rows.map((d) => (
                        <tr key={d._id}>
                          <td>{fmtIST(d.dispatchDateTime)}</td>
                          <td>{d.materialName || "—"}</td>
                          <td>{d.quantity || "—"} {d.unit || ""}</td>
                          <td>{d.vehicle?.number || "—"}</td>
                          <td><span className={statusPill(d.approvalStatus)}>{d.approvalStatus || "draft"}</span></td>
                          <td><span className={statusPill(workflowStatus(d))}>{workflowStatus(d)}</span><div className="text-muted small">{d.qualityInspection?.reportNo || ""}</div></td>
                          <td>{money(d.qualityInspection?.proposedAmount)}</td>
                          <td>
                            <strong>{d.gatePass?.code || "—"}</strong>
                            {d.gatePass?.code && <div><img src={gateQrUrl(d.gatePass.code)} alt="Gate pass QR" width="64" height="64" /></div>}
                            <div className="text-muted small">GRN: {d.grn?.number || "Pending"}</div>
                            <div className="text-muted small">Payment: {d.settlement?.status || "not-ready"}</div>
                          </td>
                          <td>{fmtIST(d.submittedAt || d.createdAt)}</td>
                          <td>
                            {d.approvalStatus === "draft" && (
                              <Button size="sm" variant="outline-primary" onClick={() => navigate(`/supplier/dispatch/edit/${d._id}`)}>
                                Edit
                              </Button>
                            )}
                            {d.qualityInspection?.reportReady && (
                              <Button size="sm" variant="outline-secondary" className="ms-1" onClick={() => downloadQualityReport(d)}>
                                PDF
                              </Button>
                            )}
                            {canRespondQuality(d) && (
                              <Button size="sm" variant="warning" className="ms-1" onClick={() => {
                                setResponseRow(d);
                                setResponseForm({ response: "agreed", remarks: "" });
                              }}>
                                Agree / Disagree
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="d-block d-md-none mt-3">
              {rows.length === 0 ? (
                <div className="text-center text-muted">No dispatches found.</div>
              ) : (
                rows.map((d) => (
                  <div className="sdh-mobile-card mb-3" key={d._id}>
                    <div className="fw-semibold">{d.materialName || "—"}</div>
                    <div className="text-muted small">{fmtIST(d.dispatchDateTime)}</div>
                    <div className="mt-2 small">
                      <div><strong>Qty:</strong> {d.quantity || "—"} {d.unit || ""}</div>
                      <div><strong>Vehicle:</strong> {d.vehicle?.number || "—"}</div>
                    <div><strong>Status:</strong> <span className={statusPill(d.approvalStatus)}>{d.approvalStatus || "draft"}</span></div>
                    <div><strong>Quality:</strong> <span className={statusPill(workflowStatus(d))}>{workflowStatus(d)}</span></div>
                    <div><strong>Amount:</strong> {money(d.qualityInspection?.proposedAmount)}</div>
                    <div>
                      <strong>Govt Payment:</strong>{" "}
                      <span className={statusPill(d.governmentPayment?.status || "unassigned")}>
                        {d.governmentPayment?.status || "unassigned"}
                      </span>
                    </div>
                    {d.governmentPayment?.paidAt && (
                      <div><strong>Paid:</strong> {fmtIST(d.governmentPayment.paidAt)}</div>
                    )}
                    <div><strong>Submitted:</strong> {fmtIST(d.submittedAt || d.createdAt)}</div>
                  </div>
                  {(d.approvalStatus === "draft" || d.qualityInspection?.reportReady || canRespondQuality(d)) && (
                    <div className="mt-2 d-flex gap-2 flex-wrap">
                      {d.approvalStatus === "draft" && (
                      <Button size="sm" variant="outline-primary" onClick={() => navigate(`/supplier/dispatch/edit/${d._id}`)}>
                        Edit Draft
                      </Button>
                      )}
                      {d.qualityInspection?.reportReady && (
                        <Button size="sm" variant="outline-secondary" onClick={() => downloadQualityReport(d)}>PDF</Button>
                      )}
                      {canRespondQuality(d) && (
                        <Button size="sm" variant="warning" onClick={() => {
                          setResponseRow(d);
                          setResponseForm({ response: "agreed", remarks: "" });
                        }}>Agree / Disagree</Button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
            </div>
          </>
        )}
      </div>
      <Modal show={!!responseRow} onHide={() => setResponseRow(null)} centered>
        <Modal.Header closeButton><Modal.Title>Quality Amount Response</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            Final quality amount: <strong>{money(responseRow?.qualityInspection?.proposedAmount)}</strong>
          </Alert>
          <Form onSubmit={async (e) => {
            e.preventDefault();
            if (!responseRow?._id) return;
            try {
              setSaving(true);
              await API.patch(`/supplier/dispatches/${responseRow._id}/quality-response`, responseForm);
              setResponseRow(null);
              setMessage("Quality response submitted.");
              load();
            } catch (err) {
              setMessage(err?.response?.data?.message || "Failed to submit quality response.");
            } finally {
              setSaving(false);
            }
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Response</Form.Label>
              <Form.Select value={responseForm.response} onChange={(e) => setResponseForm({ ...responseForm, response: e.target.value })}>
                <option value="agreed">Agree - send to Accounts</option>
                <option value="disagreed">Disagree - notify Plant Manager</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control as="textarea" rows={3} value={responseForm.remarks} onChange={(e) => setResponseForm({ ...responseForm, remarks: e.target.value })} />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setResponseRow(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Submit"}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SupplierDispatchHistory;
